import React, { useState, useEffect, useMemo } from 'react';
import {
  HelpCircle,
  Plus,
  Edit3,
  Trash2,
  ChevronDown,
  Save,
  X,
  Search,
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  RotateCcw,
  MessageSquarePlus
} from 'lucide-react';
import { FaqItem } from '../types';
import {
  DEFAULT_FAQS,
  FAQ_CATEGORIES,
  getStoredFaqs,
  saveFaqsToStore,
  subscribeToFaqs
} from '../lib/faqStore';

interface FaqInteractiveSectionProps {
  theme?: 'dark' | 'light';
  allowManage?: boolean;
  showHeader?: boolean;
}

export const FaqInteractiveSection: React.FC<FaqInteractiveSectionProps> = ({
  theme = 'light',
  allowManage = true,
  showHeader = true
}) => {
  const isDark = theme === 'dark';

  const [faqs, setFaqs] = useState<FaqItem[]>(() => getStoredFaqs());
  const [openFaqId, setOpenFaqId] = useState<string | null>(() => {
    const initial = getStoredFaqs();
    return initial.length > 0 ? initial[0].id : null;
  });

  // Search & Category filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('TODAS');

  // Manual Add / Edit Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState<string>(FAQ_CATEGORIES[0]);
  const [saving, setSaving] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);

  // Inline delete confirmation state (avoids window.confirm in iframe)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeToFaqs((updated) => {
      setFaqs(updated);
    });
    return () => unsubscribe();
  }, []);

  const showToast = (msg: string) => {
    setFeedbackMsg(msg);
    setTimeout(() => {
      setFeedbackMsg(null);
    }, 3500);
  };

  const categoriesList = useMemo(() => {
    const existingCats = Array.from(
      new Set(faqs.map((f) => f.category).filter(Boolean) as string[])
    );
    const merged = Array.from(new Set([...FAQ_CATEGORIES, ...existingCats]));
    return ['TODAS', ...merged];
  }, [faqs]);

  const filteredFaqs = useMemo(() => {
    return faqs.filter((item) => {
      if (selectedCategory !== 'TODAS' && (item.category || 'Geral & Suíte') !== selectedCategory) {
        return false;
      }
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        return (
          item.question.toLowerCase().includes(q) ||
          item.answer.toLowerCase().includes(q) ||
          (item.category || '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [faqs, selectedCategory, searchTerm]);

  const handleOpenNewForm = () => {
    setEditingId(null);
    setQuestion('');
    setAnswer('');
    setCategory(selectedCategory !== 'TODAS' ? selectedCategory : FAQ_CATEGORIES[0]);
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (item: FaqItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setEditingId(item.id);
    setQuestion(item.question);
    setAnswer(item.answer);
    setCategory(item.category || FAQ_CATEGORIES[0]);
    setIsFormOpen(true);
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setEditingId(null);
    setQuestion('');
    setAnswer('');
  };

  const handleSaveFaq = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQ = question.trim();
    const trimmedA = answer.trim();
    if (!trimmedQ || !trimmedA) return;

    setSaving(true);
    try {
      const now = new Date().toISOString();
      let updatedList: FaqItem[];

      if (editingId) {
        updatedList = faqs.map((item) =>
          item.id === editingId
            ? {
                ...item,
                question: trimmedQ,
                answer: trimmedA,
                category: category || FAQ_CATEGORIES[0],
                updatedAt: now
              }
            : item
        );
        setOpenFaqId(editingId);
        showToast('Pergunta e resposta atualizadas com sucesso!');
      } else {
        const newId = `faq-${Date.now()}`;
        const newItem: FaqItem = {
          id: newId,
          question: trimmedQ,
          answer: trimmedA,
          category: category || FAQ_CATEGORIES[0],
          order: 0,
          createdAt: now,
          updatedAt: now
        };
        updatedList = [newItem, ...faqs];
        setOpenFaqId(newId);
        showToast('Nova pergunta adicionada ao FAQ com sucesso!');
      }

      setFaqs(updatedList);
      await saveFaqsToStore(updatedList);
      handleCancelForm();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFaq = async (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const updatedList = faqs.filter((item) => item.id !== id);
    setFaqs(updatedList);
    setConfirmDeleteId(null);
    await saveFaqsToStore(updatedList);
    showToast('Pergunta removida do FAQ.');
  };

  const handleMoveFaq = async (id: string, direction: 'up' | 'down', e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    const idx = faqs.findIndex((f) => f.id === id);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= faqs.length) return;

    const copy = [...faqs];
    const temp = copy[idx];
    copy[idx] = copy[targetIdx];
    copy[targetIdx] = temp;

    setFaqs(copy);
    await saveFaqsToStore(copy);
  };

  const handleRestoreDefaults = async () => {
    setFaqs(DEFAULT_FAQS);
    setConfirmReset(false);
    await saveFaqsToStore(DEFAULT_FAQS);
    showToast('Perguntas padrão restauradas.');
  };

  return (
    <div className="space-y-6">
      {/* Top Header & Action Bar */}
      {showHeader && (
        <div
          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b ${
            isDark ? 'border-slate-800' : 'border-slate-200'
          }`}
        >
          <div className="flex items-start sm:items-center gap-3.5">
            <div
              className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
                isDark
                  ? 'bg-red-500/15 border border-red-500/30 text-red-400'
                  : 'bg-red-50 border border-red-200 text-[#E63946]'
              }`}
            >
              <HelpCircle className="w-6 h-6" />
            </div>
            <div>
              <h2
                className={`text-xl sm:text-2xl font-black tracking-tight ${
                  isDark ? 'text-white' : 'text-slate-900'
                }`}
              >
                Perguntas Frequentes (FAQ)
              </h2>
              <p className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                Tire suas dúvidas sobre redirecionamento, compras no Japão, fretes e alfândega ou cadastre novas perguntas manualmente.
              </p>
            </div>
          </div>

          {allowManage && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  if (isFormOpen && !editingId) {
                    handleCancelForm();
                  } else {
                    handleOpenNewForm();
                  }
                }}
                className="px-4 py-2.5 bg-[#E63946] hover:bg-[#d62839] active:scale-95 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar Pergunta e Resposta</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* If header is hidden (e.g. LandingPage custom header), still show the Add FAQ button */}
      {!showHeader && allowManage && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Total de {faqs.length} {faqs.length === 1 ? 'pergunta cadastrada' : 'perguntas cadastradas'}
          </div>
          <button
            type="button"
            onClick={() => {
              if (isFormOpen && !editingId) {
                handleCancelForm();
              } else {
                handleOpenNewForm();
              }
            }}
            className="px-4 py-2.5 bg-[#E63946] hover:bg-[#d62839] active:scale-95 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Adicionar Pergunta e Resposta</span>
          </button>
        </div>
      )}

      {/* Feedback Toast Banner */}
      {feedbackMsg && (
        <div
          className={`p-3.5 rounded-xl border flex items-center justify-between gap-3 text-xs sm:text-sm font-bold animate-fade-in ${
            isDark
              ? 'bg-emerald-950/70 border-emerald-700/80 text-emerald-300'
              : 'bg-emerald-50 border-emerald-200 text-emerald-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
            <span>{feedbackMsg}</span>
          </div>
          <button
            type="button"
            onClick={() => setFeedbackMsg(null)}
            className="opacity-70 hover:opacity-100 cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Manual Add / Edit FAQ Form */}
      {isFormOpen && (
        <form
          onSubmit={handleSaveFaq}
          className={`p-5 sm:p-6 rounded-2xl border space-y-4 animate-fade-in ${
            isDark
              ? 'bg-slate-950 border-slate-800 text-slate-100'
              : 'bg-slate-50 border-slate-200 text-slate-900'
          }`}
        >
          <div className="flex items-center justify-between pb-3 border-b border-slate-700/30">
            <div className="flex items-center gap-2">
              <MessageSquarePlus className="w-5 h-5 text-[#E63946]" />
              <h3 className="text-sm sm:text-base font-black">
                {editingId ? 'Editar Pergunta e Resposta' : 'Nova Pergunta Frequente (Cadastro Manual)'}
              </h3>
            </div>
            <button
              type="button"
              onClick={handleCancelForm}
              className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-200 text-slate-500'
              }`}
              title="Fechar formulário"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <label
                className={`block text-xs font-bold uppercase tracking-wider ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}
              >
                Pergunta Frequente *
              </label>
              <input
                type="text"
                required
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ex: Qual o prazo médio de entrega do Japão para o Brasil?"
                className={`w-full px-3.5 py-2.5 rounded-xl border text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#E63946] ${
                  isDark
                    ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500'
                    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
                }`}
              />
            </div>

            <div className="space-y-1.5">
              <label
                className={`block text-xs font-bold uppercase tracking-wider ${
                  isDark ? 'text-slate-300' : 'text-slate-700'
                }`}
              >
                Categoria
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={`w-full px-3.5 py-2.5 rounded-xl border text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#E63946] ${
                  isDark
                    ? 'bg-slate-900 border-slate-700 text-white'
                    : 'bg-white border-slate-300 text-slate-900'
                }`}
              >
                {FAQ_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label
              className={`block text-xs font-bold uppercase tracking-wider ${
                isDark ? 'text-slate-300' : 'text-slate-700'
              }`}
            >
              Resposta Detalhada *
            </label>
            <textarea
              required
              rows={4}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Digite aqui a resposta clara e completa que será exibida quando o cliente clicar na pergunta..."
              className={`w-full px-3.5 py-2.5 rounded-xl border text-xs sm:text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#E63946] ${
                isDark
                  ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500'
                  : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
              }`}
            />
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={handleCancelForm}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors cursor-pointer ${
                isDark
                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                  : 'bg-slate-200 hover:bg-slate-300 text-slate-700'
              }`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || !question.trim() || !answer.trim()}
              className="px-5 py-2 bg-[#E63946] hover:bg-[#d62839] disabled:opacity-50 text-white font-extrabold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>{editingId ? 'Salvar Alterações' : 'Salvar Pergunta no FAQ'}</span>
            </button>
          </div>
        </form>
      )}

      {/* Search & Category Filter Bar */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Category Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 md:pb-0">
          {categoriesList.map((cat) => {
            const active = selectedCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  active
                    ? 'bg-[#E63946] text-white shadow-xs'
                    : isDark
                    ? 'bg-slate-950 text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800'
                    : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                }`}
              >
                {cat === 'TODAS' ? 'Todas as Categorias' : cat}
              </button>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="relative w-full md:w-72 shrink-0">
          <Search
            className={`w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 ${
              isDark ? 'text-slate-500' : 'text-slate-400'
            }`}
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar dúvida ou palavra-chave..."
            className={`w-full pl-9 pr-8 py-2 rounded-xl border text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-[#E63946]/40 ${
              isDark
                ? 'bg-slate-950 border-slate-800 text-white placeholder-slate-500'
                : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'
            }`}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* FAQ Accordion List */}
      {filteredFaqs.length === 0 ? (
        <div
          className={`p-10 rounded-2xl border text-center space-y-3 ${
            isDark
              ? 'bg-slate-950/60 border-slate-800 text-slate-400'
              : 'bg-slate-50 border-slate-200 text-slate-500'
          }`}
        >
          <HelpCircle className="w-10 h-10 mx-auto opacity-40" />
          <p className="text-sm font-bold">Nenhuma pergunta encontrada para este filtro.</p>
          {allowManage && (
            <button
              type="button"
              onClick={handleOpenNewForm}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#d62839] text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer"
            >
              <Plus className="w-4 h-4" />
              <span>Adicionar Nova Pergunta</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredFaqs.map((faq, idx) => {
            const isOpen = openFaqId === faq.id;
            const isConfirmingDelete = confirmDeleteId === faq.id;

            return (
              <div
                key={faq.id}
                className={`border rounded-2xl overflow-hidden transition-all ${
                  isDark
                    ? isOpen
                      ? 'border-slate-700 bg-slate-950'
                      : 'border-slate-800/90 bg-slate-950/60 hover:border-slate-700'
                    : isOpen
                    ? 'border-slate-300 bg-white shadow-xs'
                    : 'border-slate-200 bg-slate-50/70 hover:border-slate-300'
                }`}
              >
                <div
                  onClick={() => setOpenFaqId(isOpen ? null : faq.id)}
                  className={`w-full text-left p-4 sm:p-5 flex justify-between items-start sm:items-center gap-4 transition-colors cursor-pointer ${
                    isDark ? 'hover:bg-slate-900/60' : 'hover:bg-slate-100/70'
                  }`}
                >
                  <div className="space-y-1 min-w-0 flex-1">
                    {faq.category && (
                      <div
                        className={`text-[11px] font-semibold tracking-wide ${
                          isDark ? 'text-slate-400' : 'text-slate-500'
                        }`}
                      >
                        {faq.category}
                      </div>
                    )}
                    <h3
                      className={`font-bold text-xs sm:text-sm leading-snug ${
                        isDark ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      {faq.question}
                    </h3>
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {allowManage && (
                      <div
                        className="flex items-center gap-1 mr-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={(e) => handleMoveFaq(faq.id, 'up', e)}
                          disabled={idx === 0}
                          title="Mover para cima"
                          className={`p-1.5 rounded-lg disabled:opacity-30 transition-colors cursor-pointer ${
                            isDark
                              ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                              : 'hover:bg-slate-200 text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleMoveFaq(faq.id, 'down', e)}
                          disabled={idx === filteredFaqs.length - 1}
                          title="Mover para baixo"
                          className={`p-1.5 rounded-lg disabled:opacity-30 transition-colors cursor-pointer ${
                            isDark
                              ? 'hover:bg-slate-800 text-slate-400 hover:text-white'
                              : 'hover:bg-slate-200 text-slate-500 hover:text-slate-900'
                          }`}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => handleOpenEditForm(faq, e)}
                          title="Editar pergunta e resposta"
                          className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                            isDark
                              ? 'hover:bg-slate-800 text-sky-400 hover:text-sky-300'
                              : 'hover:bg-blue-50 text-blue-600 hover:text-blue-700'
                          }`}
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>

                        {isConfirmingDelete ? (
                          <div className="flex items-center gap-1 bg-red-600/10 border border-red-500/30 px-2 py-0.5 rounded-lg">
                            <button
                              type="button"
                              onClick={(e) => handleDeleteFaq(faq.id, e)}
                              className="text-[11px] font-extrabold text-red-500 hover:underline cursor-pointer"
                            >
                              Excluir
                            </button>
                            <span className="text-slate-400">·</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteId(null);
                              }}
                              className="text-[11px] font-semibold text-slate-400 hover:text-slate-200 cursor-pointer"
                            >
                              Não
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDeleteId(faq.id);
                            }}
                            title="Excluir pergunta"
                            className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                              isDark
                                ? 'hover:bg-red-950/60 text-rose-400 hover:text-rose-300'
                                : 'hover:bg-red-50 text-red-600 hover:text-red-700'
                            }`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    )}

                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${
                        isOpen
                          ? 'rotate-180 text-[#E63946]'
                          : isDark
                          ? 'text-slate-400'
                          : 'text-slate-400'
                      }`}
                    />
                  </div>
                </div>

                {isOpen && (
                  <div
                    className={`p-4 sm:p-5 border-t text-xs sm:text-sm leading-relaxed whitespace-pre-line ${
                      isDark
                        ? 'bg-slate-900/50 border-slate-800 text-slate-300'
                        : 'bg-white border-slate-100 text-slate-600'
                    }`}
                  >
                    {faq.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer helper to restore default FAQs if needed */}
      {allowManage && (
        <div
          className={`pt-3 border-t flex flex-wrap items-center justify-between gap-2 text-xs ${
            isDark ? 'border-slate-800/80 text-slate-500' : 'border-slate-200/80 text-slate-400'
          }`}
        >
          <span>
            Dica: Clique no ícone de lápis em qualquer pergunta para editar o texto ou na seta para alterar a ordem.
          </span>
          {confirmReset ? (
            <div className="flex items-center gap-2">
              <span className={isDark ? 'text-amber-300 font-bold' : 'text-amber-700 font-bold'}>
                Restaurar perguntas originais?
              </span>
              <button
                type="button"
                onClick={handleRestoreDefaults}
                className="px-2.5 py-1 bg-amber-600 hover:bg-amber-500 text-white font-bold rounded-lg cursor-pointer"
              >
                Sim, restaurar
              </button>
              <button
                type="button"
                onClick={() => setConfirmReset(false)}
                className="px-2.5 py-1 bg-slate-700 text-slate-200 rounded-lg cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmReset(true)}
              className={`inline-flex items-center gap-1 hover:underline cursor-pointer ${
                isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Restaurar perguntas padrão</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FaqInteractiveSection;
